import React from 'react';
import { BrandAffinityData } from '../../types';
import useReveal from '../../hooks/useReveal';
import { BAR_ANIMATION_MS, REVEAL_DELAY_MS, REVEAL_DURATION_MS, toMs } from '../../constants/animation';

const BrandSection: React.FC<{ title: string; brands: any[]; icon?: string; compositionNote: string }> = ({ title, brands, icon, compositionNote }) => {
  const { ref, isVisible } = useReveal<HTMLDivElement>({ threshold: 0.2, once: true });

  // Hide section completely if no data
  if (!brands || brands.length === 0) {
    return null;
  }

  const parseIndex = (value: any) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  };
  const indices = brands.map((b) => parseIndex(b.index ?? b.value));
  const maxIndex = Math.max(120, 100, ...indices);
  const baseline = 100;
  const baselinePct = Math.min(100, (baseline / maxIndex) * 100);
  const barHeight = 'h-8';
  const trackColor = '#E5E7EB';
  const barColor = '#3B6FC4';

  return (
    <div
      ref={ref}
      className="bg-white rounded-xl shadow-md p-6"
      style={{
        transition: `transform ${toMs(REVEAL_DURATION_MS)} ease-out, opacity ${toMs(REVEAL_DURATION_MS)} ease-out`,
        transform: isVisible ? 'translateY(0)' : 'translateY(24px)',
        opacity: isVisible ? 1 : 0.25,
        transitionDelay: isVisible ? toMs(REVEAL_DELAY_MS) : '0ms',
      }}
    >
      <div className="flex items-center mb-6">
        {icon && <div className="mr-3 text-2xl">{icon}</div>}
        <div>
          <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
          <div className="text-xs text-slate-500 uppercase tracking-wide">BY INDEX · MINIMUM COMPOSITION {compositionNote}</div>
        </div>
      </div>

      <div className="space-y-5">
        {brands.map((brand: any, idx: number) => {
          const index = parseIndex(brand.index ?? brand.value);
          const widthPct = Math.min(100, (index / maxIndex) * 100);
          const delay = isVisible ? REVEAL_DELAY_MS + idx * 140 : 0;
          return (
            <div key={`${brand.name}-${idx}`} className="flex items-center gap-4">
              <div className="w-32 text-sm font-medium text-slate-800 leading-snug">{brand.name}</div>
              <div className="flex-1">
                <div className={`relative ${barHeight} rounded-full`} style={{ backgroundColor: trackColor }}>
                  <div className="absolute inset-y-0" style={{ left: `${baselinePct}%` }}>
                    <div className="w-[2px] h-full bg-white/85" />
                    <div className="absolute top-full mt-1 text-[10px] font-semibold text-slate-500" style={{ left: '50%', transform: 'translateX(-50%)' }}>
                      100
                    </div>
                  </div>
                  <div
                    className="absolute inset-y-0 left-0 rounded-full flex items-center justify-end pr-3 text-xs font-semibold text-white"
                    style={{
                      width: `${isVisible ? widthPct : 0}%`,
                      minWidth: widthPct > 0 ? '48px' : 0,
                      backgroundColor: barColor,
                      transition: `width ${toMs(BAR_ANIMATION_MS)} ease-out`,
                      transitionDelay: toMs(delay),
                    }}
                  >
                    {index}
                  </div>
                </div>
              </div>
              <div className="text-right min-w-[70px]">
                <div className="text-[11px] uppercase tracking-wide text-slate-500">Index</div>
                <div className="text-xl font-bold text-slate-900">{index}</div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="text-center mt-6 text-[11px] text-slate-400 uppercase tracking-wide">
        Online Adult Population
      </div>
    </div>
  );
};

interface BrandAffinityChartProps {
  data: BrandAffinityData;
  primaryColor?: string;
}

export default function BrandAffinityChart({ data, primaryColor = '#3B6FC4' }: BrandAffinityChartProps) {
  void primaryColor;

  // Check which sections have data
  const hasRetailers = data.retailers && data.retailers.length > 0;
  const hasAutoBrands = data.autoBrands && data.autoBrands.length > 0;
  const hasRestaurants = data.restaurants && data.restaurants.length > 0;
  const hasHotels = data.hotels && data.hotels.length > 0;
  const hasAirlines = data.airlines && data.airlines.length > 0;

  // Don't render anything if no data at all
  if (!hasRetailers && !hasAutoBrands && !hasRestaurants && !hasHotels && !hasAirlines) {
    return null;
  }

  // Collect all sections with data for flexible grid layout
  const sections = [
    hasRetailers && { key: 'retailers', element: <BrandSection title="Top Retailers" brands={data.retailers} icon="🏪" compositionNote="5%" /> },
    hasAutoBrands && { key: 'auto', element: <BrandSection title="Top Auto Brands Currently Owned" brands={data.autoBrands} icon="🚗" compositionNote="5%" /> },
    hasRestaurants && { key: 'restaurants', element: <BrandSection title="Top Restaurants Visited" brands={data.restaurants} icon="🍽️" compositionNote="5%" /> },
    hasHotels && { key: 'hotels', element: <BrandSection title="Top Hotels Tried" brands={data.hotels} icon="🏨" compositionNote="5%" /> },
    hasAirlines && { key: 'airlines', element: <BrandSection title="Top Airlines Tried" brands={data.airlines} icon="✈️" compositionNote="5%" /> },
  ].filter(Boolean) as Array<{ key: string; element: React.ReactElement }>;

  return (
    <div className="space-y-8">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Brand Affinity</h2>
            <p className="text-gray-600">BASELINE: Online Adult Population | CONTAINS: Engaged Audiences Data</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {sections.map(({ key, element }) => (
          <div key={key}>{element}</div>
        ))}
      </div>
    </div>
  );
}
